import { isQuotaError } from './storageQuota'
import { createLogger } from './logger'

const log = createLogger('storageManager')

type QuotaExceededListener = (message: string) => void

const listeners = new Set<QuotaExceededListener>()
let lastReportedTime = 0
const REPORT_THROTTLE_MS = 10000 // 10 seconds
const QUOTA_EXCEEDED_MESSAGE =
  'Storage quota exceeded. Changes cannot be saved or synchronized, risking data loss. Please free up space and check your connection to sync.'

export function registerQuotaReporter(reporter: QuotaExceededListener): () => void {
  listeners.add(reporter)
  return () => {
    listeners.delete(reporter)
  }
}

function reportQuotaExceeded(): void {
  const now = Date.now()
  if (now - lastReportedTime < REPORT_THROTTLE_MS) {
    return
  }
  lastReportedTime = now
  for (const listener of listeners) {
    try {
      listener(QUOTA_EXCEEDED_MESSAGE)
    } catch (err) {
      log.error('Error in quota listener:', err)
    }
  }
}

export function resetQuotaExceededStatus(): void {
  lastReportedTime = 0
}

export type QuotaRecoveryHandler = () => Promise<void>

let quotaRecoveryHandler: QuotaRecoveryHandler | null = null

export function registerQuotaRecoveryHandler(handler: QuotaRecoveryHandler | null): () => void {
  quotaRecoveryHandler = handler
  return () => {
    if (quotaRecoveryHandler === handler) {
      quotaRecoveryHandler = null
    }
  }
}

export function clearQuotaRecoveryHandlerForTesting(): void {
  quotaRecoveryHandler = null
}

export interface RunStorageOperationOptions {
  retryOnQuotaError?: boolean
}

/**
 * Runs a storage operation, intercepts IndexedDB write/quota errors, and reports them centrally.
 * If a QuotaRecoveryHandler is registered, attempts recovery and retries the operation once.
 */
export async function runStorageOperation<T>(
  operation: () => Promise<T>,
  options?: RunStorageOperationOptions
): Promise<T> {
  try {
    return await operation()
  } catch (error) {
    if (isQuotaError(error)) {
      if (options?.retryOnQuotaError !== false && quotaRecoveryHandler) {
        try {
          await quotaRecoveryHandler()
          // Retry once after the recovery attempt
          return await operation()
        } catch (retryError) {
          if (isQuotaError(retryError)) {
            reportQuotaExceeded()
          }
          throw retryError
        }
      }
      reportQuotaExceeded()
    }
    throw error
  }
}
